import { Divider, Stack, Typography } from "@mui/material";
import { Fragment } from "react";
import type { JSX, ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ValuationHubCoreModelRealEstateProsaWebFactorsInput } from "@/api/generatedApi";
import { CoinsHandIcon, SaleIcon } from "@/assets";
import {
  getCalculatedSliderRange,
  getIndicativeMarketValuationApproach,
  getIndicativeSubtotal,
  getMixedUseIndicativeSubtotal,
  getSliderRange,
} from "@/helpers/indicativeMarketValuation";
import { getProsaWebIndicativeFactor, getProsaWebIndicativePreliminaryValue } from "@/helpers/prosaWeb";
import { CurrentValuationState } from "@/pages/valuationPackage/valuationDetailsPage/currentValuationSlice";
import { ValuationReportState } from "@/pages/valuationReport/valuationReportPage/valuationReportSlice";
import { ObjectTypeEnum } from "@/types/refineRealEstate";

import { PanelActions } from "../pricePositioningPanel/PanelActions";
import { FactorAdjustmentCard } from "./FactorAdjustmentCard";
import { IndicativeSubtotalCard } from "./IndicativeSubtotalCard";
import { IndicativeValuationBreakdown } from "./IndicativeValuationBreakdown";
import { IndicativeValuationMetrics } from "./IndicativeValuationMetrics";
import { RentReferenceToggle } from "./RentReferenceToggle";

type MarketRentReference = "euroPerSquareMeter" | "euroPerMonth";
type FactorPayloadKey = keyof ValuationHubCoreModelRealEstateProsaWebFactorsInput;
type EditableFactorKey = Extract<FactorPayloadKey, "asset_value_factor" | "property_yield_rate" | "comparable_price">;
type FactorAdjustmentHeaderKey = "statisticalBaseValueAdjustment" | "metrics.propertyYieldRate";
type IncomeApproachReadFields = {
  property_operating_costs?: number | null;
  operating_costs_percentage?: number | null;
};
interface ApproachContentProps {
  valuationState: ValuationReportState | CurrentValuationState;
  objectType?: ObjectTypeEnum;
  isLoading?: boolean;
  onSaveFactor?: (factor: number | ValuationHubCoreModelRealEstateProsaWebFactorsInput) => Promise<void> | void;
  isSaving?: boolean;
}

interface AdjustmentCardConfig {
  key: string;
  payloadKey?: FactorPayloadKey;
  saveValue?: number;
  icon?: ReactNode;
  header: ReactNode;
  min: number;
  max: number;
  value: number;
  step?: number;
  roundValue?: boolean;
  recommendedValue?: number | null;
  unit?: string;
  toggle?: ReactNode;
  formatValue?: (value: number) => string;
  onChange?: (_event: Event, value: number | number[]) => void;
}

const getRentRangeReference = (
  recommendedValue: number | null | undefined,
  currentValue: number | null | undefined,
): number | null | undefined =>
  recommendedValue === 0 && currentValue != null && currentValue > 0 ? currentValue : recommendedValue ?? currentValue;

const getFactorAdjustmentPresentation = (
  approach: ReturnType<typeof getIndicativeMarketValuationApproach>,
  translate: (key: FactorAdjustmentHeaderKey) => string,
): { icon?: ReactNode; header: ReactNode } => {
  switch (approach) {
    case "comparableSales":
      return {
        header: translate("statisticalBaseValueAdjustment"),
      };
    case "income":
      return {
        icon: <SaleIcon />,
        header: translate("metrics.propertyYieldRate"),
      };
    case "cost":
    default:
      return {
        header: translate("statisticalBaseValueAdjustment"),
      };
  }
};

export const ApproachContent = ({
  valuationState,
  objectType,
  isLoading = false,
  onSaveFactor,
  isSaving = false,
}: ApproachContentProps): JSX.Element => {
  const { t } = useTranslation("IndicativeMarketValuationPanel");
  const { prosaWeb, propertyInformation } = valuationState;
  const isMixedUseProperty = objectType === ObjectTypeEnum.mixedUseProperty;
  const approach = getIndicativeMarketValuationApproach(objectType);
  const isComparableSalesApproach = approach === "comparableSales";
  const isIncomeApproach = approach === "income";
  const title = isComparableSalesApproach
    ? t("description.titleComparableSalesApproach")
    : isIncomeApproach
      ? t("description.titleIncome")
      : t("description.titleCostApproach");
  const livingArea = propertyInformation?.buildingData.livingArea;
  const commercialArea = propertyInformation?.buildingData.commercialArea;
  const factorPayloadKey: EditableFactorKey = isIncomeApproach
    ? "property_yield_rate"
    : isComparableSalesApproach
      ? "comparable_price"
      : "asset_value_factor";
  const marketFactor = getProsaWebIndicativeFactor(prosaWeb, objectType);
  const editableFactor = prosaWeb?.factors?.[factorPayloadKey] ?? marketFactor;
  const sliderFactor = editableFactor ? { ...marketFactor, ...editableFactor } : marketFactor;
  const preliminaryValue = getProsaWebIndicativePreliminaryValue(prosaWeb, objectType);
  const {
    min: sliderMin,
    max: sliderMax,
    value: sliderValue,
  } = getSliderRange(sliderFactor, {
    minKey: "ci95_min",
    maxKey: "ci95_max",
  });
  const incomeApproach = prosaWeb?.income_approach;
  const incomeApproachReadFields = incomeApproach as typeof incomeApproach & IncomeApproachReadFields;
  const marketComparableRent = incomeApproach?.comparable_rent;
  const comparableRent = prosaWeb?.factors?.comparable_rent
    ? { ...marketComparableRent, ...prosaWeb.factors.comparable_rent }
    : marketComparableRent;
  const residentialIncome = incomeApproach?.residential;
  const commercialIncome = incomeApproach?.commercial;
  const residentialRentFactor = prosaWeb?.factors?.comparable_rent?.residential;
  const commercialRentFactor = prosaWeb?.factors?.comparable_rent?.commercial;
  const residentialYieldRateFactor = prosaWeb?.factors?.property_yield_rate?.residential;
  const commercialYieldRateFactor = prosaWeb?.factors?.property_yield_rate?.commercial;

  const sliderStep = isComparableSalesApproach ? 0.01 : 0.001;
  const recommendedFactorValue = marketFactor?.recommended_value;

  const [isEditing, setIsEditing] = useState(false);
  const [factorSliderValue, setFactorSliderValue] = useState(sliderValue);
  const [comparableRentSliderValue, setComparableRentSliderValue] = useState(comparableRent?.value);
  const [rentReference, setRentReference] = useState<MarketRentReference>("euroPerSquareMeter");
  const [residentialRentReference, setResidentialRentReference] = useState<MarketRentReference>("euroPerSquareMeter");
  const [commercialRentReference, setCommercialRentReference] = useState<MarketRentReference>("euroPerSquareMeter");
  const [residentialRentValue, setResidentialRentValue] = useState(
    residentialRentFactor?.value ?? residentialIncome?.comparable_rent?.value,
  );
  const [commercialRentValue, setCommercialRentValue] = useState(
    commercialRentFactor?.value ?? commercialIncome?.comparable_rent?.value,
  );
  const [residentialYieldRate, setResidentialYieldRate] = useState(
    residentialYieldRateFactor?.value ?? residentialIncome?.property_yield_rate?.value,
  );
  const [commercialYieldRate, setCommercialYieldRate] = useState(
    commercialYieldRateFactor?.value ?? commercialIncome?.property_yield_rate?.value,
  );

  useEffect(() => {
    setFactorSliderValue(sliderValue);
  }, [sliderValue]);

  useEffect(() => {
    setComparableRentSliderValue(comparableRent?.value);
  }, [comparableRent?.value]);

  useEffect(() => {
    setResidentialRentValue(residentialRentFactor?.value ?? residentialIncome?.comparable_rent?.value);
    setCommercialRentValue(commercialRentFactor?.value ?? commercialIncome?.comparable_rent?.value);
    setResidentialYieldRate(residentialYieldRateFactor?.value ?? residentialIncome?.property_yield_rate?.value);
    setCommercialYieldRate(commercialYieldRateFactor?.value ?? commercialIncome?.property_yield_rate?.value);
  }, [
    commercialIncome,
    commercialRentFactor?.value,
    commercialYieldRateFactor?.value,
    residentialIncome,
    residentialRentFactor?.value,
    residentialYieldRateFactor?.value,
  ]);

  const subtotalValue = isMixedUseProperty
    ? getMixedUseIndicativeSubtotal({
        residentialRent: residentialRentValue,
        commercialRent: commercialRentValue,
        residentialArea: livingArea,
        commercialArea,
        residentialYieldRate,
        commercialYieldRate,
        operatingCosts: incomeApproachReadFields?.property_operating_costs,
        operatingCostsPercentage: incomeApproachReadFields?.operating_costs_percentage,
        residentialBaseline: {
          grossAnnualIncome: residentialIncome?.gross_annual_income,
          netAnnualIncome: residentialIncome?.net_annual_income,
        },
        commercialBaseline: {
          grossAnnualIncome: commercialIncome?.gross_annual_income,
          netAnnualIncome: commercialIncome?.net_annual_income,
        },
        landValue: incomeApproach?.land_value,
        remainingUsefulLife: incomeApproach?.remaining_useful_life,
        adjustment: incomeApproach?.property_specific_features_value,
      })
    : getIndicativeSubtotal(
        isIncomeApproach
          ? {
              propertyType: "multifamilyHouse",
              rentPerSqm: comparableRentSliderValue,
              livingArea,
              operatingCosts: incomeApproachReadFields?.property_operating_costs,
              operatingCostsPercentage: isEditing ? incomeApproachReadFields?.operating_costs_percentage : undefined,
              landValue: incomeApproach?.land_value,
              interestRate: factorSliderValue,
              capitalizationFactor: incomeApproach?.capitalization_multiplier,
              remainingUsefulLife: incomeApproach?.remaining_useful_life,
              adjustment: incomeApproach?.property_specific_features_value,
            }
          : isComparableSalesApproach
            ? {
                propertyType: "apartment",
                livingArea,
                factorValue: factorSliderValue,
              }
            : {
                propertyType: "house",
                preliminaryValue,
                factorValue: factorSliderValue,
              },
      );
  const responseMarketValue = prosaWeb?.market_value?.rounded_value ?? prosaWeb?.market_value?.value;
  const previewValue =
    isMixedUseProperty && subtotalValue != null ? Math.round(subtotalValue / 10000) * 10000 : subtotalValue;
  const displayedSubtotalValue = isEditing ? previewValue ?? responseMarketValue : responseMarketValue ?? subtotalValue;
  const adjustedValueLabel = isIncomeApproach
    ? t("incomeValue")
    : isComparableSalesApproach
      ? t("breakdown.apartment.comparisonValue")
      : t("breakdown.house.materialValue");
  const rentReferenceMultiplier =
    rentReference === "euroPerMonth" && typeof livingArea === "number" && Number.isFinite(livingArea) && livingArea > 0
      ? livingArea
      : 1;
  const displayComparableRentValue = (comparableRentSliderValue ?? 0) * rentReferenceMultiplier;
  const displayComparableRentStep = sliderStep * rentReferenceMultiplier;
  const displayComparableRentRecommendedValue =
    marketComparableRent?.recommended_value != null
      ? marketComparableRent.recommended_value * rentReferenceMultiplier
      : marketComparableRent?.recommended_value;
  const comparableRentRangeReference = getRentRangeReference(
    marketComparableRent?.recommended_value,
    comparableRent?.value,
  );
  const comparableRentFallbackRange = getCalculatedSliderRange(comparableRentRangeReference, comparableRent?.value);
  const comparableRentRange = {
    min: comparableRent?.ci95_min ?? marketComparableRent?.value_min ?? comparableRentFallbackRange.min,
    max: comparableRent?.ci95_max ?? marketComparableRent?.value_max ?? comparableRentFallbackRange.max,
  };

  const handleComparableRentSliderChange = (_event: Event, nextValue: number | number[]): void => {
    if (Array.isArray(nextValue)) return;

    setComparableRentSliderValue(nextValue / rentReferenceMultiplier);
    setIsEditing(true);
  };
  const handleFactorSliderChange = (_event: Event, nextValue: number | number[]): void => {
    if (Array.isArray(nextValue)) return;

    setFactorSliderValue(nextValue);
    setIsEditing(true);
  };
  const factorAdjustmentPresentation = getFactorAdjustmentPresentation(approach, (key) => t(key));
  const factorAdjustmentCard: AdjustmentCardConfig | null =
    isMixedUseProperty && typeof editableFactor?.value !== "number"
      ? null
      : {
          key: factorPayloadKey,
          payloadKey: isIncomeApproach ? factorPayloadKey : undefined,
          saveValue: factorSliderValue,
          icon: factorAdjustmentPresentation.icon,
          header: factorAdjustmentPresentation.header,
          min: sliderMin,
          max: sliderMax,
          value: factorSliderValue,
          step: sliderStep,
          roundValue: isComparableSalesApproach,
          recommendedValue: recommendedFactorValue,
          unit: isIncomeApproach ? "%" : undefined,
          onChange: handleFactorSliderChange,
        };
  const comparableRentAdjustmentCard: AdjustmentCardConfig | null =
    isIncomeApproach &&
    typeof comparableRentSliderValue === "number" &&
    comparableRentRange.min != null &&
    comparableRentRange.max != null
      ? {
          key: "comparable_rent",
          payloadKey: "comparable_rent",
          saveValue: comparableRentSliderValue,
          icon: <CoinsHandIcon />,
          header: t("marketRent"),
          min: comparableRentRange.min * rentReferenceMultiplier,
          max: comparableRentRange.max * rentReferenceMultiplier,
          value: displayComparableRentValue,
          unit: "€",
          step: displayComparableRentStep,
          recommendedValue: displayComparableRentRecommendedValue,
          toggle: (
            <RentReferenceToggle<MarketRentReference>
              value={rentReference}
              ariaLabel={t("marketRent")}
              options={[
                { value: "euroPerSquareMeter", label: t("rentReference.euroPerSquareMeter") },
                { value: "euroPerMonth", label: t("rentReference.euroPerMonth") },
              ]}
              onChange={setRentReference}
            />
          ),
          onChange: handleComparableRentSliderChange,
        }
      : null;
  const adjustmentCards = [isIncomeApproach ? comparableRentAdjustmentCard : null, factorAdjustmentCard].filter(
    (card): card is AdjustmentCardConfig => card !== null,
  );
  const getRentMultiplier = (reference: MarketRentReference, area?: number): number =>
    reference === "euroPerMonth" && typeof area === "number" && area > 0 ? area : 1;
  const residentialRentRangeReference = getRentRangeReference(
    residentialIncome?.comparable_rent?.recommended_value,
    residentialRentFactor?.value ?? residentialIncome?.comparable_rent?.value,
  );
  const commercialRentRangeReference = getRentRangeReference(
    commercialIncome?.comparable_rent?.recommended_value,
    commercialRentFactor?.value ?? commercialIncome?.comparable_rent?.value,
  );
  const residentialYieldRateRangeReference =
    residentialIncome?.property_yield_rate?.recommended_value ??
    residentialYieldRateFactor?.value ??
    residentialIncome?.property_yield_rate?.value;
  const commercialYieldRateRangeReference =
    commercialIncome?.property_yield_rate?.recommended_value ??
    commercialYieldRateFactor?.value ??
    commercialIncome?.property_yield_rate?.value;
  const residentialRentFallbackRange = getCalculatedSliderRange(
    residentialRentRangeReference,
    residentialRentFactor?.value ?? residentialIncome?.comparable_rent?.value,
  );
  const commercialRentFallbackRange = getCalculatedSliderRange(
    commercialRentRangeReference,
    commercialRentFactor?.value ?? commercialIncome?.comparable_rent?.value,
  );
  const residentialYieldRateFallbackRange = getCalculatedSliderRange(
    residentialYieldRateRangeReference,
    residentialYieldRateFactor?.value ?? residentialIncome?.property_yield_rate?.value,
    99.999,
  );
  const commercialYieldRateFallbackRange = getCalculatedSliderRange(
    commercialYieldRateRangeReference,
    commercialYieldRateFactor?.value ?? commercialIncome?.property_yield_rate?.value,
    99.999,
  );
  const mixedUseAdjustmentGroups = [
    {
      key: "residential",
      title: t("metrics.residential"),
      area: livingArea,
      rentMin:
        residentialRentFactor?.ci95_min ??
        residentialIncome?.comparable_rent?.value_min ??
        residentialRentFallbackRange.min,
      rentMax:
        residentialRentFactor?.ci95_max ??
        residentialIncome?.comparable_rent?.value_max ??
        residentialRentFallbackRange.max,
      rentValue: residentialRentValue,
      setRentValue: setResidentialRentValue,
      rentReference: residentialRentReference,
      setRentReference: setResidentialRentReference,
      rentRecommendedValue: residentialIncome?.comparable_rent?.recommended_value,
      yieldRateMin:
        residentialYieldRateFactor?.ci95_min ??
        residentialIncome?.property_yield_rate?.ci95_min ??
        residentialYieldRateFallbackRange.min,
      yieldRateMax:
        residentialYieldRateFactor?.ci95_max ??
        residentialIncome?.property_yield_rate?.ci95_max ??
        residentialYieldRateFallbackRange.max,
      yieldRateValue: residentialYieldRate,
      setYieldRateValue: setResidentialYieldRate,
      yieldRateRecommendedValue: residentialIncome?.property_yield_rate?.recommended_value,
    },
    {
      key: "commercial",
      title: t("metrics.commercial"),
      area: commercialArea,
      rentMin:
        commercialRentFactor?.ci95_min ??
        commercialIncome?.comparable_rent?.value_min ??
        commercialRentFallbackRange.min,
      rentMax:
        commercialRentFactor?.ci95_max ??
        commercialIncome?.comparable_rent?.value_max ??
        commercialRentFallbackRange.max,
      rentValue: commercialRentValue,
      setRentValue: setCommercialRentValue,
      rentReference: commercialRentReference,
      setRentReference: setCommercialRentReference,
      rentRecommendedValue: commercialIncome?.comparable_rent?.recommended_value,
      yieldRateMin:
        commercialYieldRateFactor?.ci95_min ??
        commercialIncome?.property_yield_rate?.ci95_min ??
        commercialYieldRateFallbackRange.min,
      yieldRateMax:
        commercialYieldRateFactor?.ci95_max ??
        commercialIncome?.property_yield_rate?.ci95_max ??
        commercialYieldRateFallbackRange.max,
      yieldRateValue: commercialYieldRate,
      setYieldRateValue: setCommercialYieldRate,
      yieldRateRecommendedValue: commercialIncome?.property_yield_rate?.recommended_value,
    },
  ];
  const getFactorInput = (): number | ValuationHubCoreModelRealEstateProsaWebFactorsInput => {
    if (!isIncomeApproach) {
      return factorSliderValue;
    }

    if (isMixedUseProperty) {
      return {
        mixed_use_factors: {
          property_yield_rate: {
            residential: residentialYieldRate != null ? { value: residentialYieldRate } : undefined,
            commercial: commercialYieldRate != null ? { value: commercialYieldRate } : undefined,
          },
          comparable_rent: {
            residential: residentialRentValue != null ? { value: residentialRentValue } : undefined,
            commercial: commercialRentValue != null ? { value: commercialRentValue } : undefined,
          },
        },
      };
    }

    return adjustmentCards.reduce<ValuationHubCoreModelRealEstateProsaWebFactorsInput>((factors, card) => {
      if (card.payloadKey && typeof card.saveValue === "number") {
        return {
          ...factors,
          [card.payloadKey]: card.saveValue,
        };
      }

      return factors;
    }, {});
  };

  return (
    <>
      <Stack>
        <Typography variant={"h4"}>{title}</Typography>
        <Typography>{t(isMixedUseProperty ? "description.textMixedUseProperty" : "description.text")}</Typography>
      </Stack>

      <IndicativeValuationMetrics
        approach={approach}
        objectType={objectType}
        valuation={prosaWeb}
        isLoading={isLoading}
      />

      <>
        {isMixedUseProperty
          ? mixedUseAdjustmentGroups.map((group) => (
              <Stack key={group.key} spacing={2.5}>
                <Typography variant="h4">{group.title}</Typography>
                <Stack divider={<Divider flexItem />} sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                  {group.rentValue != null && group.rentMin != null && group.rentMax != null ? (
                    <FactorAdjustmentCard
                      icon={<CoinsHandIcon />}
                      header={t("marketRent")}
                      min={group.rentMin * getRentMultiplier(group.rentReference, group.area)}
                      max={group.rentMax * getRentMultiplier(group.rentReference, group.area)}
                      value={group.rentValue * getRentMultiplier(group.rentReference, group.area)}
                      step={0.001 * getRentMultiplier(group.rentReference, group.area)}
                      recommendedValue={
                        group.rentRecommendedValue != null
                          ? group.rentRecommendedValue * getRentMultiplier(group.rentReference, group.area)
                          : undefined
                      }
                      unit="€"
                      toggle={
                        <RentReferenceToggle<MarketRentReference>
                          value={group.rentReference}
                          ariaLabel={t("marketRent")}
                          options={[
                            { value: "euroPerSquareMeter", label: t("rentReference.euroPerSquareMeter") },
                            { value: "euroPerMonth", label: t("rentReference.euroPerMonth") },
                          ]}
                          onChange={group.setRentReference}
                        />
                      }
                      onChange={(_event, value) => {
                        if (typeof value === "number") {
                          group.setRentValue(value / getRentMultiplier(group.rentReference, group.area));
                          setIsEditing(true);
                        }
                      }}
                    />
                  ) : null}
                  {group.yieldRateValue != null && group.yieldRateMin != null && group.yieldRateMax != null ? (
                    <FactorAdjustmentCard
                      icon={<SaleIcon />}
                      header={t("metrics.propertyYieldRate")}
                      min={group.yieldRateMin}
                      max={group.yieldRateMax}
                      value={group.yieldRateValue}
                      step={0.001}
                      recommendedValue={group.yieldRateRecommendedValue}
                      unit="%"
                      onChange={(_event, value) => {
                        if (typeof value === "number") {
                          group.setYieldRateValue(value);
                          setIsEditing(true);
                        }
                      }}
                    />
                  ) : null}
                </Stack>
              </Stack>
            ))
          : adjustmentCards.map((card, index) => (
              <Fragment key={card.key}>
                {index > 0 ? <Divider /> : null}
                <FactorAdjustmentCard
                  icon={card.icon}
                  header={card.header}
                  min={card.min}
                  max={card.max}
                  value={card.value}
                  step={card.step}
                  roundValue={card.roundValue}
                  recommendedValue={card.recommendedValue}
                  unit={card.unit}
                  toggle={card.toggle}
                  formatValue={card.formatValue}
                  onChange={card.onChange}
                />
              </Fragment>
            ))}
        <IndicativeSubtotalCard
          value={displayedSubtotalValue}
          label={isEditing ? t("preliminaryCalculation") : adjustedValueLabel}
          isLoading={isLoading}
          roundToThousands
          dataCy={
            isIncomeApproach
              ? "indicative-market-value-panel-income-value"
              : "indicative-market-value-panel-market-value"
          }
        />

        {isEditing ? (
          <PanelActions
            onStopEditing={() => {
              setFactorSliderValue(sliderValue);
              setComparableRentSliderValue(comparableRent?.value);
              setResidentialRentValue(residentialRentFactor?.value ?? residentialIncome?.comparable_rent?.value);
              setCommercialRentValue(commercialRentFactor?.value ?? commercialIncome?.comparable_rent?.value);
              setResidentialYieldRate(
                residentialYieldRateFactor?.value ?? residentialIncome?.property_yield_rate?.value,
              );
              setCommercialYieldRate(commercialYieldRateFactor?.value ?? commercialIncome?.property_yield_rate?.value);
              setIsEditing(false);
            }}
            onApply={async () => {
              if (onSaveFactor) {
                await onSaveFactor(getFactorInput());
              }

              setIsEditing(false);
            }}
            isApplying={isSaving}
          />
        ) : null}
      </>

      <IndicativeValuationBreakdown
        objectType={objectType}
        valuation={prosaWeb}
        factorValue={isEditing ? factorSliderValue : sliderValue}
        subtotalValue={subtotalValue}
        livingArea={livingArea}
        plotArea={propertyInformation?.plotData?.plotArea}
        isLoading={isLoading}
        blurValues={isEditing}
      />
    </>
  );
};
